'use client';

// The admin dashboard's phone-field dialect — a skin over the shared
// @auctogon/ui PhoneInput (BL-1241, ADR 030 § 3b Canon merge).
//
// Structure and behaviour live in the shared primitive; this file binds the
// admin appearance and nothing else. The dialect keeps admin's own values — a
// green focus ring and the gray-* neutral ramp — which are deliberately not the
// buyer site's zinc-* dialect. The label was `font-bold text-primary` until
// BL-1841: bold brand-teal, unique to this one input among admin's field
// labels, and a weight the type scale does not carry.
//
// Two things admin GAINS from the merge, both fixes the buyer site already had
// and both deliberate (BL-1241 AC 6 / RAC 5): NANP numbers typed with a leading
// "1" now lay into "(123) 456-7890" instead of being read as a country code,
// and the country dropdown portals to the body so it is no longer clipped
// inside a scrolling modal (Add Customer, Lienholder, Profile).
//
// The export style and prop signature are preserved verbatim from the 386-line
// implementation this replaces, so all nine call sites compile untouched.

import { useContext } from 'react';
import { FIELD_ERROR_CLASS, FIELD_LABEL_CLASS, FIELD_MARK_CLASS, FieldSizeContext, PhoneInput as UiPhoneInput, type PhoneInputDialect } from '@auctogon/ui/web';
import type { CountryCode } from 'libphonenumber-js';

import { SHEET_FIELD_LABEL_CLASS } from './sheet-field-classes';

export interface PhoneInputProps {
  /** E.164 phone string (e.g. "+12015550142") or empty string. */
  value: string;
  /** Called with the E.164 string on every change. Empty string when cleared. */
  onChange: (e164Value: string) => void;
  /** Default country when value is empty or has no country code. */
  defaultCountry?: CountryCode;
  /** BUG-2132 — keep a typed extension ("x12") as " ext. 12". Contact phones only. */
  allowExtension?: boolean;
  label?: string;
  placeholder?: string;
  /** Inline error message below the input. */
  error?: string;
  /** Advisory text below the input — typically why it is `disabled` (BL-1666). An `error` wins. */
  helpText?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  id?: string;
  /** Automation handle on the number input (BL-2197); opt-in, no default. */
  testId?: string;
  /** Receives the number input — React Hook Form's `field.ref` (BL-2078). */
  inputRef?: (el: HTMLInputElement | null) => void;
}

/**
 * Admin appearance. Brand colours ride the design tokens rather than hex so the
 * `brandhex/no-hardcoded-brand-hex` lint can see them; the one exception is the
 * selected row's 5% wash, because the `primary` token is a bare `var(--primary)`
 * with no `<alpha-value>` channel, so `bg-primary/5` renders nothing (the
 * documented opacity-wash carve-out in design-system-enforcement.md).
 */
const ADMIN_PHONE_DIALECT: PhoneInputDialect = {
  // BL-2930: the admin field label and its mark, the same constants FormField draws.
  label: FIELD_LABEL_CLASS,
  requiredMark: FIELD_MARK_CLASS,
  // No border-colour here — the three state slots below are mutually exclusive
  // and would otherwise collide with it at equal specificity.
  // 44px floor below `sm`, compact from `sm` up — TextInput's own breakpoint
  // pair, so a phone field matches the text fields beside it (BL-2214: the
  // number + country picker rendered 38px on a phone). Admin-only on purpose:
  // the buyer site's dialect keeps its own sizing.
  group: 'min-h-11 sm:min-h-0',
  groupResting: 'border-gray-300',
  groupError: 'border-red-300',
  groupFocused: 'ring-2 ring-secondary border-transparent',
  // BL-2939: one disabled look — gray-50, ink-500 text, no opacity (ADR 051 — Contrast Floor).
  groupDisabled: 'bg-gray-50 cursor-not-allowed',
  groupDisabledSolid: 'bg-gray-50 cursor-not-allowed',
  groupSolid: 'bg-white',
  // `self-stretch`: the number field and the country button fill the 44px row,
  // so the whole box is the tap target, not a 36px strip in its middle.
  input: 'text-primary self-stretch',
  inputDisabled: 'text-[var(--ink-500)]',
  // BL-2916 / BL-2934: FormField's error line (14/20 · 400 in --danger-500, text only).
  errorText: FIELD_ERROR_CLASS.card,
  trigger: 'border-gray-300 self-stretch',
  triggerHover: 'hover:bg-gray-50',
  triggerDisabledSolid: 'cursor-not-allowed',
  triggerDial: 'text-gray-600',
  triggerChevron: 'text-gray-400',
  dropdown: 'bg-white border-gray-200',
  searchWrap: 'border-gray-100',
  searchIcon: 'text-gray-400',
  searchInput: 'border-gray-200 focus:ring-1 focus:ring-secondary focus:border-secondary',
  groupHeader:
    'px-3 py-1.5 text-caption font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-t border-b border-gray-100 first:border-t-0',
  // Opacity wash — the documented carve-out in design-system-enforcement.md:
  // the `primary` token is a bare `var(--primary)` with no <alpha-value>
  // channel, so `bg-primary/5` emits no rule at all and the tint disappears.
  // Stays hex until the tokens gain alpha support.
  // eslint-disable-next-line brandhex/no-hardcoded-brand-hex -- opacity wash; the `primary` token is a bare var() with no <alpha-value> channel, so `bg-primary/N` renders nothing (design-system-enforcement.md § Escape hatch)
  optionSelected: 'bg-[#09514d]/5 text-primary font-medium',
  optionRest: 'text-gray-700 hover:bg-gray-50',
  optionHighlighted: 'bg-gray-100',
  // `text-primary` on the row's 5% wash (#f3f6f6) = 8.42:1 — ADR 051 non-text
  // floor is 3:1. `text-secondary` measured 1.71:1 here (BUG-1558).
  optionCheck: 'text-primary',
  emptyState: 'px-3 py-4 text-body text-gray-500 text-center',
};

/**
 * BL-2753: inside a phone sheet the field takes the sheet field look the other
 * sheet fields share — a 48px row, a 16px number, the sheet label, the sheet red
 * error border, and 14px help and error lines (FormField's touch lines).
 */
const ADMIN_PHONE_DIALECT_TOUCH: PhoneInputDialect = {
  ...ADMIN_PHONE_DIALECT,
  label: SHEET_FIELD_LABEL_CLASS,
  group: 'min-h-12',
  groupError: 'border-red-600',
  inputText: 'text-body-lg',
  errorText: FIELD_ERROR_CLASS.touch,
  helpText: 'mt-1 text-body text-gray-500',
};

/**
 * International phone input with country code selector and as-you-type
 * formatting. Emits E.164 via `onChange`.
 * @param props - {@link PhoneInputProps}
 * @returns Phone input component with flag dropdown
 */
export default function PhoneInput(props: PhoneInputProps) {
  const touch = useContext(FieldSizeContext) === 'touch';
  return <UiPhoneInput {...props} dialect={touch ? ADMIN_PHONE_DIALECT_TOUCH : ADMIN_PHONE_DIALECT} />;
}
